import DataSelectionModel from "./DataSelectionModal";
import * as yup from "yup";
import Form from "react-bootstrap/Form";
import { useDataSelectionContext } from "./DataSelectionContext";
import { useMemo } from "react";
import { WfsGetMrnRequest } from "../../types/Wfs/WfsGetMrnRequest";

interface MRNFormValues {
  access: boolean;
  outputFormat: string;
  users?: number;
}

const MRN = () => {
  const layerName = "os:mrn";
  const { layers, getLayer, wfsOrder } = useDataSelectionContext();
  const layer = useMemo(() => getLayer(layerName), [layers]);

  const initialValues = useMemo(
    () =>
      ({
        access: true,
        outputFormat: "pbf",
        users: layer?.isPremium ? 1 : undefined,
      } as MRNFormValues),
    [layer],
  );
  const schema = useMemo(
    () =>
      yup.object({
        access: yup.boolean().required(),
        outputFormat: yup.string().oneOf(["pbf", "gml"]).required(),
        users: yup.number().min(1).required(),
      }),
    [layer],
  );

  const onSubmit = (values: MRNFormValues) => {
    let filter = null;
    if (values.access) {
      filter = 'access != "no"';
    }

    wfsOrder({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      resultType: "Results",
      typeNames: layerName,
      outputFormat: values.outputFormat,
      filter: filter ?? undefined,
      users: values.users,
    } as WfsGetMrnRequest);
  };

  return (
    <DataSelectionModel
      headerText="OS Multi-Modal Routing Network"
      layer={layer}
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}>
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <>
          <Form.Group>
            <Form.Label htmlFor="access">Exclude inaccessible links</Form.Label>
            <Form.Check
              id="access"
              name="access"
              type="switch"
              checked={values.access}
              label={values.access ? "Accessible Only" : "All Links"}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="outputFormat">Format</Form.Label>
            <Form.Select
              id="outputFormat"
              name="outputFormat"
              value={values.outputFormat}
              onChange={handleChange}
              onBlur={handleBlur}>
              <option value="gml">GML 3</option>
              <option value="pbf">PBF</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="users">
              Users or terminals to licence for
            </Form.Label>
            <Form.Control
              id="users"
              name="users"
              type="number"
              min={1}
              step={1}
              value={values.users}
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={touched.users && !errors.users}
              isInvalid={touched.users && !!errors.users}
            />
            {errors.users && (
              <Form.Control.Feedback type="invalid">
                {errors.users}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </>
      )}
    </DataSelectionModel>
  );
};

export default MRN;
